import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trophy, Plus, Calendar, Users, Trash2, ChevronRight } from "lucide-react";
import { formatDateDE } from "@/lib/playerConstants";
import { toast } from "sonner";

function formatDate(iso) {
  return formatDateDE(iso);
}

export default function Tournaments() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [winners, setWinners] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tournaments");
      setTournaments(data);
      // Lazy-load winners for finished tournaments
      const w = {};
      for (const t of data) {
        if (t.finished_count === t.game_count && t.game_count > 0) {
          try {
            const { data: full } = await api.get(`/tournaments/${t.id}`);
            const { buildTable } = await import("@/lib/tournamentStats");
            const tbl = buildTable(full.teams, full.games);
            w[t.id] = tbl[0]?.name || null;
          } catch {}
        }
      }
      setWinners(w);
    } catch {
      toast.error("Turniere konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/tournaments/${confirmDelete.id}`);
      toast.success("Turnier gelöscht");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-brand-lighter mb-2">Turniermanager</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">Turniere</h1>
          <p className="text-zinc-400 text-sm mt-2">
            <span className="text-zinc-100 font-medium" data-testid="tournaments-count">{tournaments.length}</span> Turniere
          </p>
        </div>
        <Button
          onClick={() => navigate("/turniere/neu")}
          className="bg-brand hover:bg-brand-light text-white uppercase tracking-wider font-semibold rounded-sm h-11 px-6"
          data-testid="new-tournament-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Neues Turnier
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-zinc-500 font-display uppercase tracking-wider">Lädt…</div>
      ) : tournaments.length === 0 ? (
        <div className="border border-zinc-800 bg-zinc-900/50 p-12 text-center">
          <Trophy className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
          <div className="font-display text-2xl uppercase tracking-tight text-zinc-300 mb-2">Noch keine Turniere</div>
          <div className="text-sm text-zinc-500 mb-6 max-w-md mx-auto">
            Lege ein neues Turnier direkt an oder erzeuge zuerst Teams in der Team-Balance.
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button
              onClick={() => navigate("/turniere/neu")}
              className="bg-brand hover:bg-brand-light text-white uppercase tracking-wider font-semibold rounded-sm"
              data-testid="empty-new-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Neues Turnier
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/team-balance")}
              className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-brand-lighter uppercase tracking-wider font-semibold rounded-sm"
              data-testid="empty-go-balance-btn"
            >
              Zu Team-Balance
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tournaments.map((t) => {
            const pct = t.game_count === 0 ? 0 : Math.round((t.finished_count / t.game_count) * 100);
            const winner = winners[t.id];
            return (
              <Link
                key={t.id}
                to={`/turniere/${t.id}`}
                className="border border-zinc-800 bg-zinc-900/50 hover:border-brand-lighter/50 hover:bg-zinc-900 transition p-5 group flex flex-col"
                data-testid={`tournament-card-${t.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="font-display text-xl font-bold uppercase tracking-tight text-zinc-100 group-hover:text-brand-lighter transition truncate" data-testid={`tournament-name-${t.id}`}>
                    {t.name}
                  </div>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(t); }}
                    className="text-zinc-500 hover:text-red-400 p-1"
                    data-testid={`tournament-delete-${t.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-400 mb-4">
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(t.created_at)}</span>
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {t.team_count} Teams</span>
                  <span className="uppercase tracking-wider text-[10px] text-zinc-500">{t.mode === "hin_rueck" ? "Hin & Rück" : "Hinrunde"}</span>
                </div>

                <div className="mb-3">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                    <span>Fortschritt</span>
                    <span className="text-brand-lighter">{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-zinc-800 overflow-hidden">
                    <div className="h-full bg-brand-lighter transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1.5">
                    {t.finished_count} von {t.game_count} Spielen
                  </div>
                </div>

                {winner ? (
                  <div className="mt-auto flex items-center justify-between border-t border-zinc-800 pt-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-amber-400">Sieger</div>
                      <div className="text-sm font-medium text-zinc-100" data-testid={`winner-${t.id}`}>{winner}</div>
                    </div>
                    <Trophy className="h-5 w-5 text-amber-400" />
                  </div>
                ) : (
                  <div className="mt-auto flex items-center justify-end text-xs text-zinc-500 group-hover:text-brand-lighter transition">
                    Öffnen <ChevronRight className="h-3 w-3 ml-1" />
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Turnier löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmDelete?.name} wird unwiderruflich entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="tournament-delete-confirm">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
